import { useFonts } from 'expo-font';
import { Stack, useGlobalSearchParams, usePathname } from 'expo-router';
import * as SplashScreen from 'expo-splash-screen';
import { useEffect } from 'react';

import GoBackButton from '@/components/buttons/GoBackButton';
import MaintenanceOverlay from '@/components/modals/MaintenanceOverlay';
import Title from '@/components/Title';
import { useAppReadyFunctionality } from '@/hooks/useAppReadyFunctionality';
import useAuthMiddleware from '@/hooks/useAuthMiddleware';
import { i18n } from '@/utils/i18n';

export default function AppLayout() {
  const pathname = usePathname();
  const query = useGlobalSearchParams();

  const searchParams = new URLSearchParams(query as Record<string, string>).toString();
  // Add any other queryParam that comes from a deeplink
  const fullPathname = searchParams && query.influencerCode ? `${pathname}?${searchParams}` : pathname;

  const { showMaintenanceModal, appIsReady } = useAppReadyFunctionality(fullPathname);

  const { hasRouted } = useAuthMiddleware();

  const [loaded, error] = useFonts({
    'CircularStd-Book': require('@/assets/fonts/CircularStd-Book.ttf'),
    'CircularStd-Medium': require('@/assets/fonts/CircularStd-Medium.ttf'),
    'CircularStd-Bold': require('@/assets/fonts/CircularStd-Bold.ttf'),
    'CircularStd-Black': require('@/assets/fonts/CircularStd-Black.ttf')
  });

  useEffect(() => {
    if ((loaded || error) && appIsReady && hasRouted) {
      SplashScreen.hideAsync();
    }
  }, [loaded, error, hasRouted, appIsReady]);

  if (!loaded && !error) {
    return null;
  }

  return (
    <>
      <MaintenanceOverlay visible={showMaintenanceModal} />
      <Stack screenOptions={{ gestureEnabled: false }}>
        <Stack.Screen
          name="index"
          options={{
            headerShown: false
          }}
        />
        <Stack.Screen
          name="login"
          options={{
            headerShown: false
          }}
        />
        <Stack.Screen
          name="(bottomTabs)"
          options={{
            headerShown: false
          }}
        />
        <Stack.Screen
          name="filters"
          options={{
            presentation: 'modal',
            headerShown: false
          }}
        />
        <Stack.Screen
          name="light_rating_explanation"
          options={{
            presentation: 'modal',
            headerShown: false
          }}
        />
        <Stack.Screen
          name="npoDetail/[npoId]"
          options={{
            headerShown: false
          }}
        />
        <Stack.Screen
          name="programDetail"
          options={{
            headerShown: false
          }}
        />
        <Stack.Screen
          name="missionDetail/[missionId]"
          options={{
            headerShown: false
          }}
        />
        <Stack.Screen
          name="notifications"
          options={{
            headerShown: false
          }}
        />
        <Stack.Screen
          name="questions"
          options={{
            headerShown: false
          }}
        />
        <Stack.Screen
          name="create"
          options={{
            headerShown: false
          }}
        />
        <Stack.Screen
          name="edit"
          options={{
            headerShown: false
          }}
        />
        <Stack.Screen
          name="volunteerDetail/[volunteerId]"
          options={{
            headerShown: false
          }}
        />
        <Stack.Screen
          name="trendingDetail/[trendingId]"
          options={{
            headerShown: true,
            headerShadowVisible: false,
            headerBackVisible: false,
            headerTitle: () => <Title label={i18n.t('bottom_tab.engage')} />,
            headerLeft: () => <GoBackButton />
          }}
        />
        <Stack.Screen
          name="messages"
          options={{
            headerShown: false,
            headerShadowVisible: false
          }}
        />
        <Stack.Screen
          name="claimNpo"
          options={{
            headerShown: false
          }}
        />
        <Stack.Screen
          name="donationPayment"
          options={{
            headerShown: false
          }}
        />
        <Stack.Screen
          name="paymentMethods"
          options={{
            headerShown: false
          }}
        />
        <Stack.Screen
          name="npoPaymentOnboarding"
          options={{
            headerShown: false
          }}
        />
        <Stack.Screen
          name="muxPlayer"
          options={{
            headerShown: false
          }}
        />
        <Stack.Screen
          name="show"
          options={{
            headerShown: false
          }}
        />
        <Stack.Screen
          name="purchaseShow"
          options={{
            headerShown: false
          }}
        />
        <Stack.Screen
          name="confirmPurchaseShow"
          options={{
            headerShown: false
          }}
        />
        <Stack.Screen
          name="confirmPurchaseGifts"
          options={{
            headerShown: false
          }}
        />
        <Stack.Screen name="+not-found" />
      </Stack>
    </>
  );
}
